import React, { useMemo, useState } from 'react';
import Modal, { ModalActions, ModalContent, ModalTitle } from '../basic/Modal';
import LacrosseAttackField, { FieldMarker, FieldZoneFill } from './LacrosseAttackField';
import {
  ASSISTED_GOAL_COLOR,
  OUTCOME_STYLE,
  ShotChartRow,
  ShotOutcome,
  ShotZone,
  ZONE_LABELS,
  classifyShotLocation,
  formatShotTime,
  pct,
  shotDistanceYards,
  summarizeRows,
} from '../../utils/shotLocation';

// Exclusive bands matching the coach summary sheet. A shot at exactly 15 yards
// lands in the last row so the four rows add up to the section total.
const SUMMARY_BANDS: Array<{ label: string; match: (yards: number) => boolean }> = [
  { label: 'Within 8 yards', match: (yards) => yards <= 8 },
  { label: '9 to 12 yards', match: (yards) => yards > 8 && yards <= 12 },
  { label: '13 to 15 yards', match: (yards) => yards > 12 && yards < 15 },
  { label: '15 yards +', match: (yards) => yards >= 15 },
];

type ViewMode = 'dots' | 'zones' | 'both';
export type ShotSide = 'us' | 'them';

// Legend / filter buckets. Our goals split by assist; opponent assists aren't
// tracked, so their goals stay a single bucket.
type LegendKind = 'goal' | 'goal_unassisted' | 'goal_assisted' | Exclude<ShotOutcome, 'goal'>;

const LEGEND_KINDS: Record<ShotSide, LegendKind[]> = {
  us: ['goal_unassisted', 'goal_assisted', 'saved', 'missed', 'blocked'],
  them: ['goal', 'saved', 'missed', 'blocked'],
};

const LEGEND_STYLE: Record<LegendKind, { label: string; color: string }> = {
  goal: { label: 'Goal', color: OUTCOME_STYLE.goal.color },
  goal_unassisted: { label: 'Unassisted Goal', color: OUTCOME_STYLE.goal.color },
  goal_assisted: { label: 'Assisted Goal', color: ASSISTED_GOAL_COLOR },
  saved: { label: OUTCOME_STYLE.saved.label, color: OUTCOME_STYLE.saved.color },
  missed: { label: OUTCOME_STYLE.missed.label, color: OUTCOME_STYLE.missed.color },
  blocked: { label: OUTCOME_STYLE.blocked.label, color: OUTCOME_STYLE.blocked.color },
};

const ALL_KINDS = new Set<LegendKind>([...LEGEND_KINDS.us, ...LEGEND_KINDS.them]);

const legendKindOf = (row: ShotChartRow): LegendKind | null => {
  if (!row.outcome) return null;
  if (row.outcome !== 'goal') return row.outcome;
  if (row.player !== 'us') return 'goal';
  return row.assisted ? 'goal_assisted' : 'goal_unassisted';
};

interface ShotChartProps {
  /** Shot rows for either or both sides; the Us/Them toggle picks which are drawn. */
  rows: ShotChartRow[];
  loading?: boolean;
  /** Period options for the filter row, e.g. ['Q1','Q2','Q3','Q4','OT1']. Omit to hide the filter. */
  periods?: string[];
  /** Roster for the (Us) player filter. Omit to hide the filter. */
  players?: Array<{ _id: string; no?: number | string; firstName: string; lastName: string }>;
  /** Row field the player filter matches against players[]._id. */
  playerFilterKey?: 'playerId' | 'playerSeasonId';
  mode?: 'game' | 'season' | 'player';
  defaultView?: ViewMode;
  /** Show the Us / Them toggle (opponent shot chart). */
  showSideToggle?: boolean;
  /** Extra controls that sit on the same row as view / period (e.g. season game picker). */
  leadingControls?: React.ReactNode;
  /** When false, skip the outer card so the chart can sit inside another panel. */
  framed?: boolean;
  /** Opens the placement queue for the side being viewed; hidden when omitted. */
  onLocateShots?: (side: ShotSide) => void;
  emptyMessage?: string;
}

// Gold means we scored. Red means they scored. Stronger shade = higher goal rate.
function zoneResultColor(rate: number, side: ShotSide): string {
  if (rate <= 0) return '#cfcfcf';
  if (side === 'us') {
    if (rate >= 1) return '#e2ac12';
    if (rate >= 0.5) return '#f0c44a';
    return '#f8e7b0';
  }
  if (rate >= 1) return '#c62828';
  if (rate >= 0.5) return '#e07a7a';
  return '#f4cfcf';
}

function buildZoneFill(rows: ShotChartRow[], side: ShotSide): Partial<Record<ShotZone, FieldZoneFill>> {
  const summary = summarizeRows(rows);
  const fill: Partial<Record<ShotZone, FieldZoneFill>> = {};

  (Object.keys(summary.zones) as ShotZone[]).forEach((zone) => {
    const { shots, goals } = summary.zones[zone];
    if (shots === 0) return;
    fill[zone] = {
      color: zoneResultColor(goals / shots, side),
      opacity: 0.78,
      label: zone === 'crease' ? `${goals}/${shots}` : `${goals}/${shots} · ${pct(goals, shots)}`,
    };
  });

  return fill;
}

function shooterLabel(row: ShotChartRow): string {
  if (row.player !== 'us') return 'Opponent';
  const name = row.playerName || 'Team';
  const hasNumber = row.playerNumber !== null && row.playerNumber !== undefined && row.playerNumber !== '';
  return hasNumber ? `#${row.playerNumber} ${name}` : name;
}

function buildMarkers(
  rows: ShotChartRow[],
  selectedId: string | null,
  onSelect: (id: string) => void,
): FieldMarker[] {
  return rows
    .filter((r) => r.shotLocation)
    .map((r) => {
      const id = String(r._id);
      const outcome = r.outcome || 'missed';
      const kind = legendKindOf(r);
      const outcomeLabel = kind ? LEGEND_STYLE[kind].label : OUTCOME_STYLE[outcome].label;
      const style = kind ? LEGEND_STYLE[kind] : { label: outcomeLabel, color: OUTCOME_STYLE[outcome].color };
      const { zone, hash } = classifyShotLocation(r.shotLocation!.x, r.shotLocation!.y);
      const distance = shotDistanceYards(r.shotLocation!.x, r.shotLocation!.y).toFixed(1);
      return {
        key: id,
        x: r.shotLocation!.x,
        y: r.shotLocation!.y,
        outcome,
        assisted: r.player === 'us' && !!r.assisted,
        selected: id === selectedId,
        detail: {
          heading: shooterLabel(r),
          color: style.color,
          lines: [
            [outcomeLabel, r.period, formatShotTime(r.time)].filter(Boolean).join(' · '),
            `${ZONE_LABELS[zone]} · ${hash === 'inside' ? 'Inside hash' : 'Outside hash'} · ${distance} yd`,
          ],
        },
        onClick: () => onSelect(id),
      };
    });
}

const FILTER_SELECT_CLASS =
  'h-8 rounded-lg border border-[#d8dee8] bg-white px-2.5 pr-7 text-sm font-medium text-[#19133c] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19133c]/10 appearance-none';

const PillGroup: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="inline-flex h-8 shrink-0 items-center rounded-lg bg-[#eef1f6] p-0.5">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`h-7 px-3 rounded-md text-sm font-medium transition-all ${
          value === opt.value
            ? 'bg-white text-[#19133c] shadow-sm'
            : 'text-[#6b7288] hover:text-[#19133c]'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const OneField: React.FC<{
  rows: ShotChartRow[];
  view: ViewMode;
  side: ShotSide;
  label?: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}> = ({ rows, view, side, label, selectedId, onSelect }) => (
  <LacrosseAttackField
    label={label}
    markers={view !== 'zones' ? buildMarkers(rows, selectedId, onSelect) : []}
    zoneFill={view !== 'dots' ? buildZoneFill(rows, side) : undefined}
    showZoneGrid
  />
);

const InsightStrip: React.FC<{ rows: ShotChartRow[]; sideLabel: string }> = ({ rows, sideLabel }) => {
  const summary = summarizeRows(rows);
  if (summary.locatedShots === 0) return null;

  const zoneEntries = (Object.keys(summary.zones) as ShotZone[])
    .map((zone) => ({ zone, ...summary.zones[zone] }))
    .filter((z) => z.goals > 0)
    .sort((a, b) => b.goals - a.goals);
  const topZone = zoneEntries[0];
  const totalGoals = summary.byOutcome.goal;

  const insideRate = pct(summary.insideHash.goals, summary.insideHash.shots);
  const outsideRate = pct(summary.outsideHash.goals, summary.outsideHash.shots);

  return (
    <div className="flex flex-col gap-1 text-sm text-[#19133c] px-1 pt-1">
      {topZone && totalGoals > 0 && (
        <div>
          <span className="font-semibold">{sideLabel}</span>
          <span className="text-[#6b7288]"> · </span>
          {topZone.goals} of {totalGoals} goals from{' '}
          <span className="font-semibold">{ZONE_LABELS[topZone.zone]}</span>
        </div>
      )}
      {(summary.insideHash.shots > 0 || summary.outsideHash.shots > 0) && (
        <div className="text-[#6b7288]">
          Inside hash {insideRate} ({summary.insideHash.goals}/{summary.insideHash.shots})
          <span className="text-[#c5ccd8]"> · </span>
          Outside hash {outsideRate} ({summary.outsideHash.goals}/{summary.outsideHash.shots})
        </div>
      )}
    </div>
  );
};

const ShotChart: React.FC<ShotChartProps> = ({
  rows,
  loading = false,
  periods,
  players,
  playerFilterKey = 'playerId',
  mode = 'game',
  defaultView = mode === 'game' ? 'dots' : 'zones',
  showSideToggle = false,
  leadingControls,
  framed = true,
  onLocateShots,
  emptyMessage = 'No shots recorded yet.',
}) => {
  const [view, setView] = useState<ViewMode>(defaultView);
  const [side, setSide] = useState<ShotSide>('us');
  const [period, setPeriod] = useState<string>('all');
  const [playerId, setPlayerId] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [activeKinds, setActiveKinds] = useState<Set<LegendKind>>(new Set(ALL_KINDS));

  const legendKinds = LEGEND_KINDS[side];

  const toggleKind = (kind: LegendKind) => {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        // keep at least one bucket visible on this side
        if (legendKinds.filter((k) => next.has(k)).length === 1) return prev;
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  };

  const sideRows = useMemo(() => rows.filter((r) => r.player === side), [rows, side]);

  const shooters = useMemo(() => {
    const shotsByPlayer = new Map<string, number>();
    rows.forEach((r) => {
      if (r.player !== 'us') return;
      const key = String(r[playerFilterKey] ?? '');
      if (key) shotsByPlayer.set(key, (shotsByPlayer.get(key) || 0) + 1);
    });
    return (players || [])
      .filter((p) => shotsByPlayer.has(String(p._id)))
      .map((p) => ({ ...p, shots: shotsByPlayer.get(String(p._id)) || 0 }));
  }, [rows, players, playerFilterKey]);

  const showPlayerFilter = side === 'us' && shooters.length > 0;

  const activeRows = useMemo(() => {
    return sideRows.filter((r) => {
      const kind = legendKindOf(r);
      if (!kind || !activeKinds.has(kind)) return false;
      if (period !== 'all' && r.period !== period) return false;
      if (showPlayerFilter && playerId !== 'all' && String(r[playerFilterKey] ?? '') !== playerId) return false;
      return true;
    });
  }, [sideRows, period, playerId, playerFilterKey, showPlayerFilter, activeKinds]);

  const kindCounts = useMemo(() => {
    const counts: Partial<Record<LegendKind, number>> = {};
    activeRows.forEach((r) => {
      const kind = legendKindOf(r);
      if (kind) counts[kind] = (counts[kind] || 0) + 1;
    });
    return counts;
  }, [activeRows]);

  const unlocatedCount = useMemo(() => sideRows.filter((r) => !r.shotLocation).length, [sideRows]);
  const sideLabel = side === 'us' ? 'Us' : 'Them';
  const toggleSelected = (id: string) => setSelectedId((current) => (current === id ? null : id));

  return (
    <div
      className={`w-full flex flex-col gap-4 ${
        framed
          ? 'rounded-2xl border border-[#e6ebf2] bg-white p-4 sm:p-5 shadow-[0_1px_2px_rgba(25,19,60,0.04)]'
          : ''
      }`}
    >
      {/* One toolbar: side / game / view / period / player */}
      <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
        {showSideToggle && (
          <PillGroup
            options={[
              { value: 'us', label: 'Us' },
              { value: 'them', label: 'Them' },
            ]}
            value={side}
            onChange={(v) => setSide(v as ShotSide)}
          />
        )}
        {leadingControls}
        <PillGroup
          options={[
            { value: 'dots', label: 'Shots' },
            { value: 'zones', label: 'Zones' },
            { value: 'both', label: 'Both' },
          ]}
          value={view}
          onChange={(v) => setView(v as ViewMode)}
        />
        {periods && periods.length > 0 && (
          <div className="relative shrink-0">
            <select value={period} onChange={(e) => setPeriod(e.target.value)} className={FILTER_SELECT_CLASS}>
              <option value="all">All Periods</option>
              {periods.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <SelectCaret />
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowSummary(true)}
          title="Shot summary"
          aria-label="Shot summary"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#d8dee8] bg-white text-[#19133c] shadow-sm hover:bg-[#f4f6fa]"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M1.5 13.5h13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M3.5 11V8.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M7 11V5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M10.5 11V3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M13.5 11V6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        {showPlayerFilter && (
          <div className="relative w-44 shrink-0">
            <select value={playerId} onChange={(e) => setPlayerId(e.target.value)} className={`${FILTER_SELECT_CLASS} w-full`}>
              <option value="all">All Players</option>
              {shooters.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.no ? `#${p.no} ` : ''}
                  {`${p.firstName} ${p.lastName}`.trim()} ({p.shots})
                </option>
              ))}
            </select>
            <SelectCaret />
          </div>
        )}
      </div>

      {/* Outcome legend / filter chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        {legendKinds.map((kind) => {
          const style = LEGEND_STYLE[kind];
          const active = activeKinds.has(kind);
          return (
            <button
              key={kind}
              type="button"
              onClick={() => toggleKind(kind)}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium transition-colors ${
                active
                  ? 'bg-[#f4f6fa] text-[#19133c] ring-1 ring-[#d8dee8]'
                  : 'text-[#9aa3b2] hover:text-[#6b7288]'
              }`}
            >
              <span
                className={`inline-block h-2 w-2 shrink-0 ${
                  kind === 'saved' ? 'rotate-45' : kind === 'blocked' ? '' : 'rounded-full'
                }`}
                style={{ backgroundColor: style.color }}
              />
              {style.label}
              <span className={active ? 'text-[#8a93a6]' : 'text-[#c0c6d2]'}>{kindCounts[kind] || 0}</span>
            </button>
          );
        })}
      </div>

      {unlocatedCount > 0 && onLocateShots && (
        <button
          type="button"
          onClick={() => onLocateShots(side)}
          className="w-full text-left rounded-xl bg-[#f7f8fb] px-3 py-2 text-sm font-medium text-[#19133c] hover:bg-[#eef1f6] transition-colors"
        >
          {unlocatedCount} {side === 'them' ? 'opponent ' : ''}shot{unlocatedCount === 1 ? '' : 's'} need a location —
          tap to place them
        </button>
      )}

      {loading ? (
        <div className="w-full py-16 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#19133c]" />
        </div>
      ) : activeRows.length === 0 ? (
        <div className="w-full py-12 text-center text-[#8a93a6] text-sm">{emptyMessage}</div>
      ) : (
        <div className="max-w-2xl mx-auto w-full flex flex-col gap-2">
          <OneField rows={activeRows} view={view} side={side} selectedId={selectedId} onSelect={toggleSelected} />
          <InsightStrip rows={activeRows} sideLabel={sideLabel} />
        </div>
      )}

      <ShotSummaryModal
        open={showSummary}
        side={side}
        sideLabel={sideLabel}
        rows={activeRows}
        onClose={() => setShowSummary(false)}
      />
    </div>
  );
};

const wholePct = (numerator: number, denominator: number) =>
  !denominator ? '0%' : `${Math.round((numerator / denominator) * 100)}%`;

const tenthPct = (numerator: number, denominator: number) =>
  !denominator ? '0.0%' : `${((numerator / denominator) * 100).toFixed(1)}%`;

const averageYards = (rows: ShotChartRow[]) => {
  if (!rows.length) return '—';
  const total = rows.reduce((sum, row) => sum + shotDistanceYards(row.shotLocation!.x, row.shotLocation!.y), 0);
  return (total / rows.length).toFixed(1);
};

const ShotSummaryModal: React.FC<{
  open: boolean;
  side: ShotSide;
  sideLabel: string;
  rows: ShotChartRow[];
  onClose: () => void;
}> = ({ open, side, sideLabel, rows, onClose }) => {
  const ofOutcome = (outcome: ShotOutcome) => rows.filter((row) => row.outcome === outcome);
  const goals = ofOutcome('goal');
  const unassisted = goals.filter((row) => !row.assisted).length;
  const assisted = goals.length - unassisted;
  const showBlocked = rows.some((row) => row.outcome === 'blocked');

  const sections: Array<{ label: string; tint: string; rows: ShotChartRow[]; assistSplit?: boolean }> = [
    { label: 'Goals', tint: '#d9ead3', rows: goals, assistSplit: side === 'us' },
    { label: 'Missed', tint: '#f8d7a8', rows: ofOutcome('missed') },
    { label: 'Saved', tint: '#d4e4f7', rows: ofOutcome('saved') },
    ...(showBlocked ? [{ label: 'Blocked', tint: '#f4cccc', rows: ofOutcome('blocked') }] : []),
  ];

  return (
    <Modal isOpen={open} onClose={onClose} maxWidth="md">
      <ModalTitle onClose={onClose}>Shot Summary</ModalTitle>
      <ModalContent>
        <p className="mb-3 text-sm text-[#6b7288]">{sideLabel} shots on the field right now.</p>
        <div className="flex flex-col gap-3 text-sm text-[#19133c]">
          {sections.map((section) => {
            const located = section.rows.filter((row) => row.shotLocation);
            const missingLocation = section.rows.length - located.length;
            return (
              <div key={section.label} className="overflow-hidden rounded-md" style={{ backgroundColor: section.tint }}>
                <div className="grid grid-cols-[1fr_3.25rem_4.75rem] items-baseline px-3 py-1.5 font-bold">
                  <span>{section.label}</span>
                  <span className="text-right">{section.rows.length}</span>
                  <span />
                </div>
                {section.assistSplit && (
                  <>
                    <SummaryLine label="Unassisted" middle={String(unassisted)} detail={wholePct(unassisted, goals.length)} />
                    <SummaryLine label="Assisted" middle={String(assisted)} detail={wholePct(assisted, goals.length)} />
                  </>
                )}
                <div className="grid grid-cols-[1fr_auto] items-baseline px-3 py-1">
                  <span className="pl-4">Average distance</span>
                  <span className="text-right">{located.length ? `${averageYards(located)} Yards` : '—'}</span>
                </div>
                {SUMMARY_BANDS.map((band) => {
                  const count = located.filter((row) =>
                    band.match(shotDistanceYards(row.shotLocation!.x, row.shotLocation!.y)),
                  ).length;
                  return (
                    <SummaryLine
                      key={band.label}
                      label={band.label}
                      middle={String(count)}
                      detail={tenthPct(count, section.rows.length)}
                    />
                  );
                })}
                {missingLocation > 0 && (
                  <SummaryLine
                    label="No location"
                    middle={String(missingLocation)}
                    detail={tenthPct(missingLocation, section.rows.length)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </ModalContent>
      <ModalActions>
        <button type="button" onClick={onClose} className="rounded-md bg-[#19133c] px-4 py-2 font-semibold text-white">
          Close
        </button>
      </ModalActions>
    </Modal>
  );
};

const SummaryLine: React.FC<{ label: string; middle?: string; detail?: string }> = ({ label, middle, detail }) => (
  <div className="grid grid-cols-[1fr_3.25rem_4.75rem] items-baseline px-3 py-1">
    <span className="pl-4">{label}</span>
    <span className="text-right">{middle ?? ''}</span>
    <span className="text-right">{detail ?? ''}</span>
  </div>
);

const SelectCaret: React.FC = () => (
  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#8a93a6]">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

export default ShotChart;
